<script setup lang="ts">
/**
 * Admin shell — two-level navigation (groups, then pages). The page is
 * gated server-side by `_require_admin`; if the caller isn't in
 * `ADMIN_GROUP`, each child component surfaces the 403 inline.
 *
 * Why no router: there's exactly one extra route here, plus a hash for
 * which page to land on. Pulling vue-router for that is overkill. The hash
 * looks like `#/admin?tab=usage`; missing tab = `overview`. The groups, the
 * pages and the old-link mapping live in `admin-nav.ts`.
 */
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { adminReadOnly } from "../admin-read-only";
import {
  type GroupId,
  type NavGroup,
  type Tab,
  VIEWER_HIDDEN,
  groupOf,
  resolveTab,
  visibleGroups as navGroups,
} from "../admin-nav";
import AdminPeople from "./AdminPeople.vue";
import AdminAiRequests from "./AdminAiRequests.vue";
import AdminAssistants from "./AdminAssistants.vue";
import AdminOverview from "./AdminOverview.vue";
import AdminBudgets from "./AdminBudgets.vue";
import AdminFeedbackReview from "./AdminFeedbackReview.vue";
import AdminGlossary from "./AdminGlossary.vue";
import AdminGovernance from "./AdminGovernance.vue";
import AdminDataSources from "./AdminDataSources.vue";
import AdminLogs from "./AdminLogs.vue";
import AdminUsage from "./AdminUsage.vue";
import AdminAudit from "./AdminAudit.vue";
import AdminAccessRequests from "./AdminAccessRequests.vue";
import AdminScheduledJobs from "./AdminScheduledJobs.vue";
import AdminPlatformJobs from "./AdminPlatformJobs.vue";
import TeamsPanel from "./TeamsPanel.vue";
import { getFeedbackOverview, listAiRequests } from "../api/admin";
import { getAccessRequestSummary } from "../api/accessRequests";

const props = defineProps<{
  devUser: string;
  /** ADR 0072 — the Viewer role: read the admin area, change nothing. */
  readOnly?: boolean;
}>();
const emit = defineEmits<{ (e: "exit"): void }>();

const tab = ref<Tab>(resolveTab(window.location.hash));

const visibleGroups = computed(() => navGroups(!!props.readOnly));
const group = computed<GroupId>(() => groupOf(tab.value));
const activeGroup = computed<NavGroup | undefined>(() =>
  visibleGroups.value.find((g) => g.id === group.value),
);
/** The second row shows only when the open group has more than one page. */
const hasSubnav = computed(() => (activeGroup.value?.tabs.length ?? 0) > 1);

// Watches the tab too: a link into a hidden page (the Audit tab's "logs"
// link, an old bookmark) lands a viewer on People instead of on a 403.
watch(
  [() => !!props.readOnly, tab],
  ([ro, t]) => {
    adminReadOnly.value = ro;
    if (ro && VIEWER_HIDDEN.has(t)) setTab("people");
  },
  { immediate: true },
);

/** The page last open in each group, so switching groups and back returns
 *  to it rather than to the group's first page. */
const lastInGroup = ref<Partial<Record<GroupId, Tab>>>({});
watch(
  tab,
  (t) => {
    lastInGroup.value = { ...lastInGroup.value, [groupOf(t)]: t };
  },
  { immediate: true },
);

function setTab(t: Tab) {
  tab.value = t;
  window.location.hash = `#/admin?tab=${t}`;
}

function setGroup(g: NavGroup) {
  const remembered = lastInGroup.value[g.id];
  const target =
    remembered && g.tabs.some((t) => t.id === remembered)
      ? remembered
      : g.tabs[0].id;
  if (target !== tab.value) setTab(target);
}

function onHashChange() {
  if (window.location.hash.startsWith("#/admin")) {
    tab.value = resolveTab(window.location.hash);
  } else {
    emit("exit");
  }
}

/**
 * Arrow keys move between the tabs of one row (WAI-ARIA tabs pattern, manual
 * activation: focus moves, Enter or Space opens — so arrowing past a heavy
 * page does not load it). Home/End jump to the ends. Tab leaves the row
 * from the selected tab (roving tabindex), so the keyboard reaches the page
 * in two stops, not seventeen.
 */
function onTabKey(e: KeyboardEvent) {
  if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
  const items = Array.from(
    (e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
      '[role="tab"]',
    ),
  );
  const i = items.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  e.preventDefault();
  let next = i;
  if (e.key === "ArrowLeft") next = (i - 1 + items.length) % items.length;
  if (e.key === "ArrowRight") next = (i + 1) % items.length;
  if (e.key === "Home") next = 0;
  if (e.key === "End") next = items.length - 1;
  items[next].focus();
}

/**
 * Pending work per page, so the nav itself answers "is anything waiting for
 * me" without opening anything. Only pages with a genuine queue get one — a
 * badge on every tab is decoration, and decoration on a nav is noise. A
 * group shows the sum of its pages' badges, so a queue inside a group that
 * is not open is still visible from the first row.
 */
const counts = ref<Partial<Record<Tab, number>>>({});
const feedbackTimer = ref<number | null>(null);

function groupCount(g: NavGroup): number {
  return g.tabs.reduce((n, t) => n + (counts.value[t.id] ?? 0), 0);
}
function groupCountTitle(g: NavGroup): string {
  return g.tabs
    .filter((t) => counts.value[t.id])
    .map((t) => {
      const n = counts.value[t.id] ?? 0;
      return t.countTitle?.(n) ?? `${t.label}: ${n}`;
    })
    .join("; ");
}

async function refreshCounts() {
  // Independent, and each failure is silent: a 403 for a non-admin or a
  // backend hiccup should leave the badge off, never break the nav.
  const [feedback, requests, access] = await Promise.allSettled([
    // No window: "is anything waiting for me" is not a 30-day question, and
    // a badge that quietly ignored older unreviewed feedback could not be
    // cleared to zero — which is the one thing a badge has to be able to do.
    getFeedbackOverview("", props.devUser),
    listAiRequests(props.devUser, "pending"),
    getAccessRequestSummary(props.devUser),
  ]);
  counts.value = {
    "feedback-review":
      feedback.status === "fulfilled" ? feedback.value.totals.new_down : 0,
    "ai-requests": requests.status === "fulfilled" ? requests.value.length : 0,
    "access-requests":
      access.status === "fulfilled" ? access.value.open_now : 0,
  };
}

onMounted(() => {
  window.addEventListener("hashchange", onHashChange);
  // A tab that changes its own queue says so, rather than leaving the badge
  // wrong until the next poll.
  window.addEventListener("choir:admin-counts-stale", refreshCounts);
  void refreshCounts();
  feedbackTimer.value = window.setInterval(refreshCounts, 120_000);
});
onUnmounted(() => {
  adminReadOnly.value = false;
  window.removeEventListener("hashchange", onHashChange);
  window.removeEventListener("choir:admin-counts-stale", refreshCounts);
  if (feedbackTimer.value !== null) window.clearInterval(feedbackTimer.value);
});
</script>

<template>
  <div class="admin-page">
    <header class="admin-page__header">
      <h1>Admin</h1>
      <button class="link" @click="emit('exit')">← back to chat</button>
    </header>
    <nav class="admin-nav" aria-label="Admin">
      <div
        class="admin-nav__groups"
        role="tablist"
        aria-label="Admin areas"
        @keydown="onTabKey"
      >
        <!-- Group badges sum only the pages in the VISIBLE group, so a
             viewer never sees a count for a page they do not get. -->
        <button
          v-for="g in visibleGroups"
          :id="`admin-group-${g.id}`"
          :key="g.id"
          :class="['group', group === g.id && 'group--active']"
          role="tab"
          :aria-selected="group === g.id"
          :tabindex="group === g.id ? 0 : -1"
          :aria-controls="
            group === g.id && hasSubnav ? 'admin-subnav' : 'admin-panel'
          "
          @click="setGroup(g)"
        >
          {{ g.label }}
          <span
            v-if="groupCount(g)"
            class="tab__badge"
            :title="groupCountTitle(g)"
          >
            {{ groupCount(g) }}
            <span class="sr-only">waiting</span>
          </span>
        </button>
      </div>
      <div
        v-if="activeGroup && hasSubnav"
        id="admin-subnav"
        class="admin-nav__tabs"
        role="tablist"
        :aria-label="`${activeGroup.label} pages`"
        @keydown="onTabKey"
      >
        <button
          v-for="t in activeGroup.tabs"
          :id="`admin-tab-${t.id}`"
          :key="t.id"
          :class="['tab', tab === t.id && 'tab--active']"
          role="tab"
          :aria-selected="tab === t.id"
          :tabindex="tab === t.id ? 0 : -1"
          aria-controls="admin-panel"
          @click="setTab(t.id)"
        >
          {{ t.label }}
          <span
            v-if="counts[t.id]"
            class="tab__badge"
            :title="
              t.countTitle?.(counts[t.id] ?? 0) ?? `${counts[t.id]} waiting`
            "
          >
            {{ counts[t.id] }}
            <span class="sr-only">waiting</span>
          </span>
        </button>
      </div>
    </nav>
    <p v-if="props.readOnly" class="admin-page__read-only" role="note">
      Read-only role — you can look at everything here but change nothing.
      Conversations, documents and other content are not shown.
    </p>
    <section
      id="admin-panel"
      class="admin-page__panel"
      role="tabpanel"
      :aria-labelledby="hasSubnav ? `admin-tab-${tab}` : `admin-group-${group}`"
    >
      <AdminPeople v-if="tab === 'people'" :dev-user="props.devUser" />
      <AdminOverview v-else-if="tab === 'overview'" :dev-user="props.devUser" />
      <AdminAssistants
        v-else-if="tab === 'assistants'"
        :dev-user="props.devUser"
      />
      <TeamsPanel v-else-if="tab === 'teams'" :dev-user="props.devUser" />
      <AdminScheduledJobs
        v-else-if="tab === 'scheduled-jobs'"
        :dev-user="props.devUser"
      />
      <AdminGlossary v-else-if="tab === 'glossary'" :dev-user="props.devUser" />
      <AdminUsage v-else-if="tab === 'usage'" :dev-user="props.devUser" />
      <AdminBudgets v-else-if="tab === 'budgets'" :dev-user="props.devUser" />
      <AdminGovernance
        v-else-if="tab === 'governance'"
        :dev-user="props.devUser"
      />
      <AdminGovernance
        v-else-if="tab === 'evidence'"
        view="evidence"
        :dev-user="props.devUser"
      />
      <AdminAudit v-else-if="tab === 'audit'" :dev-user="props.devUser" />
      <AdminAccessRequests
        v-else-if="tab === 'access-requests'"
        :dev-user="props.devUser"
      />
      <AdminLogs v-else-if="tab === 'logs'" :dev-user="props.devUser" />
      <AdminDataSources
        v-else-if="tab === 'data-sources'"
        :dev-user="props.devUser"
      />
      <AdminPlatformJobs
        v-else-if="tab === 'platform-jobs'"
        :dev-user="props.devUser"
      />
      <AdminAiRequests
        v-else-if="tab === 'ai-requests'"
        :dev-user="props.devUser"
      />
      <AdminFeedbackReview
        v-else-if="tab === 'feedback-review'"
        :dev-user="props.devUser"
      />
    </section>
  </div>
</template>

<style scoped>
.admin-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  /* A glass card like every other surface in the app; this was the one
     flat --color-bg panel with a hard border. */
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  flex: 1;
  min-width: 0;
}
.admin-page__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-2);
}
/* Two rows, both wrapping: never a sideways scroll, at any width. Groups are
   pills (a choice of area); the pages under them are underlined tabs (a
   choice within it), so the two levels never read as one long row. */
.admin-nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.admin-nav__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.group {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--color-border);
  background: transparent;
  border-radius: var(--radius-pill);
  padding: 0.3rem 0.85rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-muted);
  cursor: pointer;
  white-space: nowrap;
}
.group:hover {
  color: var(--color-ink);
}
.group--active {
  color: var(--color-ink);
  font-weight: 600;
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 10%, transparent);
}
.admin-nav__tabs {
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.25rem;
  border-bottom: 1px solid var(--color-border);
}
.tab {
  white-space: nowrap;
  border: none;
  background: transparent;
  padding: 0.45rem 0.8rem;
  font-size: 0.92rem;
  cursor: pointer;
  color: var(--color-muted);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}
.tab:hover {
  color: var(--color-ink);
}
.group:focus-visible,
.tab:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}
.tab:focus-visible {
  border-radius: var(--radius-sm);
}
.tab--active {
  color: var(--color-ink);
  border-bottom-color: var(--color-accent);
  font-weight: 600;
}
.tab__badge {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.admin-page__panel {
  flex: 1;
  min-height: 0;
  min-width: 0;
}
.admin-page__read-only {
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border, currentColor);
  background: var(--color-surface-alt);
  font-size: 0.88rem;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 0.92rem;
  padding: 0;
}
/* Phone width: tighter pills so the seven groups take two short rows. */
@media (max-width: 480px) {
  .admin-page {
    padding: var(--space-3);
  }
  .group {
    padding: 0.25rem 0.65rem;
    font-size: 0.85rem;
  }
  .tab {
    padding: 0.4rem 0.55rem;
    font-size: 0.88rem;
  }
}
</style>
